import { FC } from "preact/compat";
import { useRef } from "preact/hooks";
import { useComputed, useSignal, useSignalEffect } from "@preact/signals";
import { format, isSameMonth, isSameYear } from "date-fns";
import { useApp } from "~/core";
import { createDonutChart } from "~/chart";
import { getHoursDistribution } from "~/chart-data";
import { useAppTime, useCanvasInit, useLocal } from "~/hooks";
import { formatMinutes } from "~/utils";
import { useMobileDashboard } from "../../context";
import { BreakdownProps } from "./interfaces";
import FunctionsIcon from "@material-design-icons/svg/outlined/functions.svg";
import Loader from "~/assets/loader.svg";
import cn from "classnames";

const EMPTY_CHART_DATA: ReturnType<typeof getHoursDistribution> = {
    colors: [],
    labels: [],
    longLabels: [],
    values: [],
};

const getTitle = (
    date: Date,
    day: Date | null,
    today: Date,
    mode: "month" | "day",
    accumulated: boolean,
) => {
    if (day && mode === "day") {
        if (accumulated) {
            return `Accumulated till ${format(day, "MMM dd")}`;
        }

        return format(day, "dd MMM");
    }

    if (isSameMonth(date, today) && accumulated) {
        return "Accumulated till today";
    }

    if (isSameYear(date, today)) {
        return format(date, "MMMM");
    }

    return format(date, "MMMM yyyy");
};

const Breakdown: FC<BreakdownProps> = ({ by, className }) => {
    const { api, mobile } = useApp();
    const { loading, mode, selectedDate, selectedDay } = useMobileDashboard();
    const chartRef = useRef<ReturnType<typeof createDonutChart> | null>(null);
    const today = useAppTime("day");
    const chartData = useSignal(EMPTY_CHART_DATA);
    const storage = useLocal(`v1_m-dashboard_${by}`, {
        accumulated: false,
    });
    const hover = useSignal(-1);
    const containerWidth = useSignal(0);
    const restLoading = useComputed(
        () => api.project.loading.value || api.feeCode.loading.value,
    );
    const ref = useCanvasInit((canvas) => {
        chartRef.current = createDonutChart({
            canvas,
            data: chartData.peek(),
            gradient: true,
            onHover: (index) => {
                hover.value = index;
                mobile.impact("light");
            },
        });

        return () => {
            if (chartRef.current) {
                chartRef.current.destroy();
                chartRef.current = null;
            }
        };
    });

    useSignalEffect(() => {
        void mode.value;
        void selectedDate.value;
        void selectedDay.value;

        hover.value = -1;
        chartRef.current?.hover(-1);
    });

    useSignalEffect(() => {
        const isLoading = loading.value || restLoading.value;
        const date = selectedDate.value;
        const day = selectedDay.value;
        const mod = mode.value;
        const acc = storage.value.accumulated;
        const now = today.value;

        void api.time.report.reports.value;

        if (isLoading) {
            return;
        }

        const isDifferentMonth = !isSameMonth(date, now) && mod === "month";

        chartData.value = getHoursDistribution(
            (mod === "day" ? day : date) ?? date,
            by,
            acc && !isDifferentMonth
                ? "accumulated"
                : mod === "day"
                  ? "day"
                  : "month",
        );

        if (chartRef.current) {
            chartRef.current.update(chartData.peek());
        }
    });

    const data = chartData.value;
    const sum = data.values.reduce((a, b) => a + b, 0);
    const isEmpty =
        !loading.value && !restLoading.value && data.values.length === 0;
    const isLoading =
        loading.value || (restLoading.value && data.values.length === 0);
    const selectedValue = data.values[hover.value] ?? 0;
    const valueText =
        hover.value === -1
            ? formatMinutes(sum * 60)
            : formatMinutes(selectedValue * 60);
    const accumulated = storage.value.accumulated;

    return (
        <div
            ref={(el) => {
                if (el) {
                    containerWidth.value = el.clientWidth - 32;
                }
            }}
            className={className}
        >
            <div className="flex">
                <div className="relative size-60 flex-shrink-0">
                    <canvas ref={ref} className="w-full h-full" />
                    <div className="flex flex-col justify-center items-center absolute inset-0">
                        {isLoading ? (
                            <Loader
                                width="24"
                                height="24"
                                className="text-sky-500"
                            />
                        ) : (
                            <>
                                <p className="text-xs text-gray-500">
                                    {getTitle(
                                        selectedDate.value,
                                        selectedDay.value,
                                        today.value,
                                        mode.value,
                                        accumulated,
                                    )}
                                </p>
                                <p
                                    className={cn(
                                        "text-xs truncate transition-all duration-200",
                                        hover.value === -1 ? "h-0" : "h-4",
                                    )}
                                >
                                    {data.labels[hover.value] ?? ""}
                                </p>
                                {isEmpty ? (
                                    <p className="text-sm text-gray-500">
                                        No data
                                    </p>
                                ) : (
                                    <p className="text-sm font-bold">
                                        {valueText}
                                    </p>
                                )}
                            </>
                        )}
                    </div>
                </div>
                <div className="border-l border-gray-200 max-h-60 overflow-y-auto rounded-r-2xl w-full">
                    <div className="w-full border-b border-gray-200 px-2 flex items-center justify-between py-1 capitalize font-bold text-gray-500">
                        <span>{by}s</span>
                        <label
                            className={cn(
                                "flex rounded-full p-0.5 border transition duration-200 gap-x-1 fill-current items-center",
                                accumulated
                                    ? "bg-sky-600 text-white border-sky-600"
                                    : "border-gray-300 text-gray-400",
                            )}
                        >
                            <FunctionsIcon width="20" height="20" />
                            <input
                                type="checkbox"
                                className="opacity-0 size-0 absolute"
                                checked={accumulated}
                                onClick={({ currentTarget }) => {
                                    storage.value = {
                                        accumulated: currentTarget.checked,
                                    };
                                    mobile.impact("light");
                                }}
                            />
                        </label>
                    </div>
                    {data.labels.map((label, index) => (
                        <button
                            key={label}
                            type="button"
                            onClick={() => {
                                const val = hover.peek() === index ? -1 : index;

                                hover.value = val;
                                chartRef.current?.hover(val);
                                mobile.impact("light");
                            }}
                            className={cn(
                                "flex w-full px-1 py-2 items-center gap-x-1 text-left",
                                hover.value === index && "bg-gray-100",
                            )}
                        >
                            <div
                                style={{ backgroundColor: data.colors[index] }}
                                className="h-4 w-2 rounded flex-shrink-0"
                            />
                            <p className="truncate text-xs">
                                <strong className="font-bold">
                                    {Math.round(
                                        (data.values[index] / sum) * 100,
                                    )}
                                    %
                                </strong>{" "}
                                <span>{label}</span>
                            </p>
                        </button>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default Breakdown;
